import './App.css';
import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import Cookies from "js-cookie";

export const raffleExplanation = "Signing up for reminders related to the film will earn you an entry into the raffle for \
  official Catastrobee merch. Upon submission, you will also receive a shareable link. For every person that signs up for \
  reminders via your link, you will receive 5 additional entries into the raffle! Signing up through a referral link will \
  gain you 3 entries into the raffle instead of just 1!";

// Helper to format phone numbers as (XXX) XXX-XXXX
const formatPhone = (digits) => {
  if (!digits) return '';
  if (digits.length > 6) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6,10)}`;
  if (digits.length > 3) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
  return `(${digits}`;
};

// Extract digits and limit to 10
const getDigits = (formatted) => formatted.replace(/\D/g, '').slice(0, 10);

function Signup() {
  const queryParams = new URLSearchParams(window.location.search);
  const rid = queryParams.get('rid');
  const successfulSignUpCookieName = "catastrobee_successful_signup";
  const shareableLinkCookieName = "catastrobee_shareable_link";


  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    tshirtSize: '',
    reserveTshirt: false
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [signedUpSuccess, setSignedUpSuccess] = useState(false);
  const [shareableLink, setShareableLink] = useState("");
  const [checkingCookie, setCheckingCookie] = useState(true);
  const [copied, setCopied] = useState(false); 

  const phoneInputRef = useRef(null);



  useEffect(() => {
    const successCookie = Cookies.get(successfulSignUpCookieName);
    const linkCookie = Cookies.get(shareableLinkCookieName);

    if (successCookie === "true" && linkCookie) {
      setSignedUpSuccess(true);
      setShareableLink(linkCookie);
    } else {
      setSignedUpSuccess(false);
      setShareableLink("");
    }

    setCheckingCookie(false); // done checking
  }, []);

  // Handles changes to all inputs
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'phone') {
      const input = e.target;
      const selectionStart = input.selectionStart;

      // Extract digits, limit to 10
      const digits = getDigits(value);
      const formatted = formatPhone(digits);

      setFormData(prev => ({ ...prev, phone: formatted }));

      // Adjust cursor
      setTimeout(() => {
        if (phoneInputRef.current) {
          let newPos = selectionStart;
          const countNonDigitsBeforeCursor = (value.slice(0, selectionStart).match(/\D/g) || []).length;
          newPos -= countNonDigitsBeforeCursor;
          const formattedPrefixLength = formatted.length - digits.length;
          newPos += formattedPrefixLength;
          if (newPos > formatted.length) newPos = formatted.length;
          phoneInputRef.current.setSelectionRange(newPos, newPos);
        }
      }, 0);

    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text");
    const digits = getDigits(paste); // automatically truncated to 10
    setFormData(prev => ({ ...prev, phone: formatPhone(digits) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    const digits = getDigits(formData.phone);
    const payloadPhone = `+1${digits}`;

    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      phone_number: payloadPhone,
      referrer_id: rid,
      reserve_shirt: formData.reserveTshirt,
      shirt_size: formData.tshirtSize,
    };

    try {
      const response = await fetch(
        "https://tz95rg7de4.execute-api.us-east-2.amazonaws.com/prod/subscribe",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(response.status);
      } else {
        const data = await response.json();
        Cookies.set(successfulSignUpCookieName, "true", { expires: 365 });
        Cookies.set(shareableLinkCookieName, data.shareable_link, { expires: 365 });
        setSignedUpSuccess(true);
        setShareableLink(data.shareable_link);
      }
      await response.json();
    } catch (e) {
      const errorMessage = `There was a problem submitting your request (${e.message})`;

      if (e.message === "404") {
        setErrorMessage(`${errorMessage}. Are you by chance trying to cheat a friend out of their raffle entries? If so, that is despicable behavior
          and you should genuinely feel bad about yourself. It's appalling to think someone would stoop so low. If this was an accident, fix your URL and try again.`);
      } else {
        setErrorMessage(`${errorMessage}. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!shareableLink) return;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      // Modern way
      navigator.clipboard.writeText(shareableLink).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => fallbackCopy());
    } else {
      // Fallback for older browsers / mobile
      fallbackCopy();
    }
  };

  const fallbackCopy = () => {
    const input = document.createElement("input");
    input.value = shareableLink;
    document.body.appendChild(input);
    input.select();
    try {
      document.execCommand("copy");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert("Could not copy link, please copy manually.");
    }
    document.body.removeChild(input);
  };


  function getFormHtml() {
    return ( <form onSubmit={handleSubmit}>
                <div className="field-row">
                  <div className="field">
                    <label htmlFor="firstName">First name</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      maxLength="50"
                      autoComplete="given-name"
                      required
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="lastName">Last name</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      maxLength="50"
                      autoComplete="family-name"
                      required
                    />
                  </div>
                </div>

                <div className="field-row">
                  <div className="field">
                    <label htmlFor="phone">Phone number</label>
                    <div className="input-container phone-wrapper">
                      <span className="prefix">+1</span>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        ref={phoneInputRef}
                        value={formData.phone}
                        onChange={handleChange}
                        onPaste={handlePaste}
                        placeholder="(555) 123-4567"
                        required
                      />
                    </div>
                  </div>

                  <div className="field">
                    <label htmlFor="tshirtSize">T-shirt size</label>
                    <select
                      id="tshirtSize"
                      name="tshirtSize"
                      value={formData.tshirtSize}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select size</option>
                      <option value="S">S</option>
                      <option value="M">M</option>
                      <option value="L">L</option>
                      <option value="XL">XL</option>
                      <option value="XXL">XXL</option>
                    </select>
                  </div>
                </div>

                <p className="smaller-text">
                  By entering my phone number, I understand I am opting in to receive 2 text messages per week
                  related to Catastrobee's upcoming release. Message and data rates may apply.
                  Reply STOP to opt out. See our <a href='/privacy'>privacy policy</a> and <a href='/terms'>terms of service</a>.
                </p>

                <div className="checkbox-row">
                  <input
                    type="checkbox"
                    id="reserveTshirt"
                    name="reserveTshirt"
                    checked={formData.reserveTshirt}
                    onChange={handleChange}
                  />
                  <label htmlFor="reserveTshirt">
                    I would like to reserve a t-shirt that I can purchase regardless of the raffle results
                  </label>
                </div>

                <button
                  type="submit"
                  className={`submit-btn ${loading ? "loading" : ""}`}
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>

                {errorMessage && <p className="error-message">{errorMessage}</p>}
              </form>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <img
          src="/CatastrobeeTitleCard.png"
          alt="Catastrobee Title"
          className="title-image"
        />
      </header>
      <main>
        <div className={`content signup-content ${signedUpSuccess ? "signed-up" : ""}`}>
          <div className="image-column">
            <img src="/shirt.png" alt="Black artwork" />
          </div>
          <div className="form-wrapper">
            <div className="form-column">
              {checkingCookie ? (
                <p>Loading...</p> // Or nothing, if you prefer
              ) : !signedUpSuccess ? (
                <div>
                  <p className="form-title">Sign up for reminders and win merch!</p>
                  <p className="smaller-text">{raffleExplanation}</p>
                  <p className="smaller-text">
                    Please note that signing up with a fake phone number will not gain you entries into the raffle, 
                    and signing up multiple times with the same phone number will not increase your chances of winning.
                  </p>
                  {getFormHtml()}
                </div>
              ) : (
                <div>
                  <p className="form-title">Thank you for signing up!</p>
                  <p className="smaller-text">
                    You've successfully been entered into the raffle and have subscribed to promotional messages.
                  </p>
                  <p className="smaller-text">
                    Share your unique link below to earn 5 more raffle entries for each friend that signs up through it!
                  </p>
                  <div className="shareable-link-wrapper">
                    <input
                      type="text"
                      value={shareableLink}
                      readOnly
                      className="shareable-link-input"
                    />
                    <button
                      type="button"
                      onClick={copyToClipboard}
                      className="copy-btn"
                    >
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>

                </div>
              )}
              <p className="back-link"><Link to="/">Back to home</Link></p>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}

export default Signup;
